import { Component, OnInit, OnDestroy, ElementRef, ViewChild, HostListener } from "@angular/core";
import { Subject } from "rxjs";
import { Store, select } from "@ngrx/store";
import { IAppState } from "@edp-store/state/app.state";
import { filter, take, takeUntil } from "rxjs/operators";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import { SCREEN_SIZES } from "@edp-enums/screen-sizes.enum";
import moment from "moment";
import { Router } from "@angular/router";
import { NgbModalOptions } from "@ng-bootstrap/ng-bootstrap";
import { EdpECodeGenerationModalComponent } from "@edp-components/edp-e-code-generation-modal/edp-e-code-generation-modal.component";
import { EdpModalService } from "@edp-components/edp-modal";
import { TranslateService } from "@ngx-translate/core";
import { GetCounterGeneratedCodes, GetGeneratedCodes } from "@edp-store/actions/benefits.actions";
import { generatedCodesCards, getCounterGeneratedCodes } from "@edp-store/selectors/benefits.selectors";
import { IBenefitsCounter } from "@edp-models/benefits.model";
import { getContract } from "@edp-store/selectors/contract.selectors";
import { Contract } from "@edp-components/edp-contract";
import { FormatDecimalValue } from "@edp-utils/format-decimal-value.utils";
import { CookieService } from "ngx-cookie-service";
import { LanguageTypeEnum } from "@edp-enums/language.enum";
import { LanguageService } from "@edp-services/language.service";

const colors = {
    "edp-neutral-01": "#222222",
    "edp-main-01": "#28FF52",
    "edp-seaweed-green-01": "#28FF52",
    "edp-seaweed-green-04": "#90979F",
    "edp-marine-blue-04": "#90979F",
};

@Component({
    selector: "edp-widget-savings",
    templateUrl: "./edp-widget-counter-savings.component.html",
    styleUrls: ["./edp-widget-counter-savings.component.scss"],
})
export class EdpWidgetCounterSavingsComponent implements OnInit, OnDestroy {
    public counter: IBenefitsCounter = {
        benefits: [],
        full_value: 0,
        used_value: 0,
    };
    public fullValueFormatted = "";
    public usedValueFormatted = "";
    private unsubscribe = new Subject();
    @ViewChild("chartElement") chartElement: ElementRef<any>;
    private chart: am4charts.GaugeChart;
    public mobileSize = false;
    public textCode: string;
    public codesLength: number;
    public contract: Contract;
    public namePack = "";
    constructor(
        private store: Store<IAppState>,
        private router: Router,
        private edpModalService: EdpModalService,
        private translateService: TranslateService,
        private languageService: LanguageService
    ) {}

    @HostListener("window:resize")
    public onResize() {
        const backup = this.mobileSize;
        this.mobileSize = false;
        if (window.innerWidth <= SCREEN_SIZES.MD) {
            this.mobileSize = true;
        }
        if (backup !== this.mobileSize) {
            this.chartConstruction();
        }
    }

    public ngOnInit() {
        this.onResize();
        this.store
            .pipe(takeUntil(this.unsubscribe), select(getCounterGeneratedCodes))
            .subscribe((counterPartnerBenefits: IBenefitsCounter) => {
                if (counterPartnerBenefits) {
                    this.counter = counterPartnerBenefits;
                    this.fullValueFormatted = FormatDecimalValue.getFormatted(this.counter.full_value, false);
                    this.usedValueFormatted = FormatDecimalValue.getFormatted(this.counter.used_value, false);
                    this.expiringCodesConstruction();
                    this.chartConstruction();
                }
            });
        this.store.dispatch(new GetCounterGeneratedCodes());
        this.store.pipe(takeUntil(this.unsubscribe), select(getContract)).subscribe(contract => {
            if (contract && contract.ContractId !== this.contract?.ContractId) {
                this.contract = contract;
                const haspack: any = contract.getPackProperties(contract.HasPack);
                this.namePack = this.translateService.instant(haspack.name);
            }
        });
    }



    expiringCodesConstruction(): void {
        this.codesLength = this.counter.benefits.reduce((sum, i) => sum + i.codes.length, 0);
        const names = this.counter.benefits.map(({ name }) => name);
        if (!this.codesLength) {
            this.textCode = this.translateService.instant("pt.edp.edponline.widget.counter.benefits.codes.empty", {
                TOTAL: this.fullValueFormatted,
            });
        } else {
            if (this.codesLength >= 4) {
                this.textCode = this.translateService.instant("pt.edp.edponline.widget.counter.benefits.codes", {
                    LENGTH: this.codesLength,
                });
            } else if (this.codesLength >= 2 && names.length === 1) {
                this.textCode = this.translateService.instant("pt.edp.edponline.widget.counter.benefits.codes.two.and.one.name", {
                    LENGTH: this.codesLength,
                    NAME: names[0],
                });
            } else if (this.codesLength >= 2) {
                const last_name = names.pop();
                this.textCode = this.translateService.instant("pt.edp.edponline.widget.counter.benefits.codes.two", {
                    LENGTH: this.codesLength,
                    NAMES: names.join(", "),
                    LAST_NAME: last_name,
                });
            } else if (this.codesLength >= 1) {
                this.textCode = this.translateService.instant("pt.edp.edponline.widget.counter.benefits.codes.one", {
                    LENGTH: this.codesLength,
                    NAME: this.counter.benefits[0].name,
                    CODE: this.counter.benefits[0].codes[0],
                    DATE: moment(this.counter.benefits[0].date_validation)
                        .locale(this.languageService.getLocale())
                        .format("DD MMM YYYY"),
                });
            }
        }
    }

    public chartConstruction(): void {
        if (this.chartElement) {
            this.chart = am4core.create(this.chartElement.nativeElement, am4charts.GaugeChart);
            this.chart.paddingLeft = this.mobileSize ? 55 : 0;
            this.chart.paddingRight = this.mobileSize ? 55 : 0;
            this.chart.paddingBottom = this.mobileSize ? 5 : 0;
            this.chart.paddingTop = 28;
            this.chart.radius = am4core.percent(100);
            this.chart.innerRadius = am4core.percent(85);

            const axis: am4charts.ValueAxis<am4charts.AxisRenderer> = this.chart.xAxes.push(new am4charts.ValueAxis());
            axis.min = 0;
            axis.max = !!this.counter.full_value ? this.counter.full_value : 100;
            axis.strictMinMax = true;

            const range0: am4charts.ValueAxisDataItem = axis.axisRanges.create();
            range0.value = 0;
            range0.endValue = 0;
            range0.axisFill.fillOpacity = 1;
            range0.axisFill.fill = am4core.color(this.mobileSize ? colors["edp-main-01"] : colors["edp-seaweed-green-01"]);

            const range1 = axis.axisRanges.create();
            range1.axisFill.fillOpacity = 1;
            range1.axisFill.fill = am4core.color(this.mobileSize ? colors["edp-marine-blue-04"] : colors["edp-seaweed-green-04"]);
            range1.endValue = axis.max;
            range1.value = range0.endValue;
            range1.label.text = "";

            if (this.counter.used_value > 0) {
                range0.endValue = this.counter.used_value;
                range1.value = this.counter.used_value;
                range1.label.text = `${FormatDecimalValue.getFormatted(this.counter.used_value, false)} €`;
            }

            axis.renderer.grid.template.disabled = true;
            axis.renderer.labels.template.fontFamily = "Mulish";
            axis.renderer.labels.template.fontSize = "10px";
            axis.renderer.labels.template.fill = am4core.color(colors["edp-main-01"]);
            axis.renderer.labels.template.adapter.add("text", value => {
                if (!!this.mobileSize && this.counter.used_value > 0 && Number(value) === range1.endValue) {
                    return this.fullValueFormatted;
                } else if (!!this.mobileSize && !this.counter.used_value && Number(value) === 0) {
                    return FormatDecimalValue.getFormatted(0);
                } else {
                    return "";
                }
            });
        }
    }

    navigateToBenefits() {
        this.router.navigate(["beneficios"]);
    }

    public ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }

    public openCodeGeneration(): void {
        this.store.dispatch(new GetGeneratedCodes());
        this.store
            .pipe(
                select(generatedCodesCards),
                filter(cards => !!cards && cards.length > 0),
                take(1)
            )
            .subscribe((cards: any[]) => {
                if (cards) {
                    const partner_id = this.counter.benefits[0].partner_id;
                    const code = this.counter.benefits[0].codes[0];
                    const item = cards.find(
                        i => i.id === partner_id && (i.generatedCode.code === code || i.generatedCodes?.find(c => c.code === code))
                    );
                    if (item) {
                        const modalData = {
                            showClose: true,
                            collection: item,
                            type: "expiringCode",
                            action: "share",
                        };

                        const options: NgbModalOptions = {
                            windowClass: "modal-mobile modal-code-generation",
                            keyboard: true,
                        };

                        this.edpModalService.custom(EdpECodeGenerationModalComponent, options, modalData);
                    }
                }
            });
    }
}
